<template>
  <div
    class="custom-select"
    :class="customClass"
    :tabindex="tabindex"
    @blur="closeSelect($event)"
  >
    <div
      class="selected"
      :style="customStyles"
      :class="{ open: open, invalid: isInvalid }"
      @click="toggleOpen"
    >
      <span :class="{ placeholder: !selected }">
        {{ selected ? selected : placeholder }}
      </span>
      <arrowDown class="arrow" />
    </div>
    <div class="items" :class="{ selectHide: !open, 'position-top': showAbove }">
      <div
        v-for="(option, i) of options"
        :key="i"
        @click="onSelect(option)"
      >
        {{ option.name }}
      </div>
      <div
        v-if="addInput"
        class="new-option-container"
        @click.stop
        @mousedown.stop
      >
        <input
          ref="newOptionInput"
          v-model="newOptionValue"
          class="new-option-input"
          placeholder="Add new group"
          @click.stop
          @mousedown.stop
          @keyup.enter="addNewOption"
          @focus.stop
        >
      </div>
    </div>
  </div>
</template>

<script>
import ArrowDown from '@/components/icons/ArrowDown';

export default {
  name: 'CustomSelect',
  components: {ArrowDown},
  props: {
    typeSelect: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    default: {
      type: String,
      default: '',
    },
    tabindex: {
      type: Number,
      required: false,
      default: 0,
    },
    customStyles: {
      type: Object,
      default: () => ({})
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    addInput: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selected: this.default
        ? this.default
        : this.placeholder
          ? null
          : this.options.length > 0
            ? this.options[0].name
            : null,
      open: false,
      newOptionValue: '',
      showAbove: false,
    };
  },
  watch: {
    default(newVal) {
      if (newVal !== this.selected) {
        this.selected = newVal;
      }
    }
  },
  mounted() {
    if (this.typeSelect !== 'filterUser') {
      this.$emit('input', this.selected);
    }

    // Add resize event listener
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    // Remove resize event listener
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    toggleOpen() {
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => {
          this.checkPosition();
          this.focusNewOptionInput();
        });
      }
    },

    checkPosition() {
      const selectElement = this.$el;
      const dropdownElement = selectElement.querySelector('.items');

      if (selectElement && dropdownElement) {
        const selectRect = selectElement.getBoundingClientRect();
        const dropdownHeight = dropdownElement.offsetHeight;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - selectRect.bottom;

        // If there's not enough space below, show dropdown above
        this.showAbove = spaceBelow < dropdownHeight && selectRect.top > dropdownHeight;
      }
    },
    focusNewOptionInput() {
      if (this.$refs.newOptionInput) {
        this.$refs.newOptionInput.focus();
      }
    },
    closeSelect(event) {
      // Check if the focus is moving to the input field
      if (event && event.relatedTarget && event.relatedTarget.classList.contains('new-option-input')) {
        // Don't close the dropdown if the focus is moving to the input field
        return;
      }

      // Check if the active element is the input field
      if (document.activeElement && document.activeElement.classList.contains('new-option-input')) {
        // Don't close the dropdown if the active element is the input field
        return;
      }

      // Check if the event target is the input field
      if (event && event.target && event.target.classList.contains('new-option-input')) {
        // Don't close the dropdown if the event target is the input field
        return;
      }

      this.open = false;
      this.showAbove = false;
    },
    clearSelection() {
      this.selected = null;
    },
    onSelect(option) {
      const emitValue = this.typeSelect === 'filterUser' ? option.id : option.name;
      this.selected = option.name;
      this.open = false;
      this.showAbove = false;
      this.$emit('input', emitValue);
      this.$emit('selected', emitValue);
    },
    addNewOption() {
      if (this.newOptionValue.trim()) {
        this.selected = this.newOptionValue.trim();
        this.open = false;
        this.showAbove = false;
        this.$emit('input', this.newOptionValue.trim());
        this.$emit('selected', this.newOptionValue.trim());
        this.$emit('addNew', this.newOptionValue.trim());
        this.newOptionValue = '';
      }
    },

    handleResize() {
      if (this.open) {
        this.checkPosition();
      }
    },
  },
};
</script>

<style scoped>

.custom-select {
  position: relative;
  width: 100%;
  outline: none;
}

.custom-select .selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  background-color: var(--white-color);
  border-radius: 79px;
  padding: 0 15px;
  min-height: 35px;
  color: var(--secondary-black-color) !important;
  cursor: pointer;
  border: 1px solid var(--input-placeholder-value-color) !important;
  user-select: none;
  font-weight: 500;
}

.custom-select .selected:hover {
  filter: drop-shadow(0px 0px 5px rgba(200, 205, 212, 0.5));
}

.custom-select .selected.open svg {
  transform: rotateX(180deg);
}

.selected span {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected svg {
  margin-left: auto;
}

.selected svg path {
  fill: var(--input-placeholder-value-color);
  cursor: pointer;
  transition: .3s all;
}

.selected:hover svg path {
  fill: var(--light-gray);
}

.custom-select .selected.open {
  background: var(--white-color);
  border: 1px solid var(--input-placeholder-value-color);
  box-sizing: border-box;
  box-shadow: 0 0 15px rgba(5, 51, 48, 0.06);
  border-radius: 20px;
}


.custom-select .items {
  margin-top: 5px;
  color: var(--secondary-text-color);
  overflow-x: hidden;
  overflow-y: scroll;
  background-color: var(--white-color);
  position: absolute;
  left: 0;
  right: 0;
  z-index: 20;
  border-radius: 4px !important;
  border: 1px solid var(--background-button-disabled-color);
  height: fit-content;
  max-height: 250px;
  box-shadow: -2px 4px 10px 0px #4D6A9326;
  scrollbar-color: var(--background-color) transparent;
  scrollbar-width: thin;
}

.custom-select .items div {
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}

.custom-select .items div {
  text-overflow: ellipsis;
  overflow: hidden;
}

.custom-select .items div:hover {
  background: #F8FCFC;
  width: 100%;
}

.selectHide {
  display: none;
}

.items.position-top {
  bottom: 100%;
  top: auto;
  margin-top: 0;
  margin-bottom: 5px;
}

.support-form .selected {
  padding: 15px 25px;
}

.support-form .selected svg path {
  fill: var(--light-gray);
}

.support-form .selected:hover {
  filter: none;
}

.selectMessages .selected {
  padding: 9px 15px;
}

.selectMessages .items div {
  font-size: 12px;
}

.placeholder {
  font-size: 14px;
  font-weight: 500;
  color: var(--light-gray);
}

.arrow {
  width: 15px;
  height: 15px;
  max-width: 15px;
  max-height: 15px;
  transition: .5s all;
}

.new-option-container {
  padding: 10px 16px;
  border-top: 1px solid var(--input-placeholder-value-color);
}

.new-option-input {
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid var(--input-placeholder-value-color);
  font-size: 14px;
  color: var(--secondary-black-color);
}

.selected.invalid {
  border: 1px solid red !important;
}

.new-option-input:focus {
  outline: none;
  border-color: var(--border-color);
  box-shadow: 0 0 3px rgba(0, 123, 255, 0.25);
}
</style>
